import { gql } from '@apollo/client';

import { formatCurrency } from '@/lib/utils';

export const GET_ACCOUNT_PITCHBOOK = gql`
  query GetPitchbooksByAccount($accountId: ID!) {
    pitchbooks(accountId: $accountId) {
      edges {
        node {
          id
          accountId {
            id
            name
          }
          currencyId {
            id
            name
          }
          pbAum
          pbAumBase
          pbDryPowder
          pbDryPowderBase
          pbBusinessStatus
          pbCompanyStatus
          pbEmployees
          pbFinancingStatus
          pbFirmSubType
          pbFirmType
          pbFormerName
          pbOwnershipStatus
          pbGlobalRegion
          pbGlobalSubRegion
          pbPrimaryUsRegion
          pbIndustriesAll
          pbIndustry
          pbIndustryCode
          pbIndustryGroup
          pbIndustrySector
          pbInvestorStatus
          pbNoOfActiveInvestments
          pbLtmNoOfInvestments
          pbLastInvDate
          pbLastInvLeadPartner
          pbLastInvLeadPartnerEmail
          pbLastInvLeadPartnerPhone
          pbLastInvLeadPartnerTitle
          pbLastInvLocation
          pbLastInvName
          pbLastInvPrimaryIndustry
          pbLastInvSize
          pbLastInvSizeBase
          pbLastInvStatus
          pbLastInvType
          pbDateOfLastRound
          pbLastRoundStatus
          pbLastRoundType
          pbLastSyncDate
          pbLastSyncUser {
            id
            name
          }
          pitchbookId
          pitchbookLink
          pbPreferredCompanyValuation
          pbPreferredDealSize
          pbPreferredEbitda
          pbPreferredGeographies
          pbPreferredIndustries
          pbPreferredInvestmentAmount
          pbPreferredInvestmentHorizon
          pbPreferredInvestmentTypes
          pbPreferredOther
          pbPreferredRevenue
          pbPreferredVerticals
          pbPrimaryContact
          pbPrimaryContactEmail
          pbPrimaryContactPhone
          pbPrimaryContactTitle
          pbYearFounded
          preferredMethodOfContact
        }
      }
    }
  }
`;

// GraphQL Response Types
export interface GetAccountPitchbookData {
  pitchbooks: {
    edges: {
      node: PitchbookNode;
    }[];
  };
}

export interface GetAccountPitchbookVariables {
  accountId: string;
}

// UI Data Type
export interface PitchBookData {
  pbAum: string;
  pbAumBase: string;
  pbBusinessStatus: string;
  pbCompanyStatus: string;
  pbDateOfLastRound: string;
  pbDryPowder: string;
  pbDryPowderBase: string;
  pbEmployees: null | number;
  pbFinancingStatus: string;
  pbFirmSubType: string;
  pbFirmType: string;
  pbFormerName: string;
  pbGlobalRegion: string;
  pbGlobalSubRegion: string;
  pbIndustriesAll: string;
  pbIndustry: string;
  pbIndustryCode: string;
  pbIndustryGroup: string;
  pbIndustrySector: string;
  pbInvestorStatus: string;
  pbLastInvDate: string;
  pbLastInvLeadPartner: string;
  pbLastInvLeadPartnerEmail: string;
  pbLastInvLeadPartnerPhone: string;
  pbLastInvLeadPartnerTitle: string;
  pbLastInvLocation: string;
  pbLastInvName: string;
  pbLastInvPrimaryIndustry: string;
  pbLastInvSize: string;
  pbLastInvSizeBase: string;
  pbLastInvStatus: string;
  pbLastInvType: string;
  pbLastRoundStatus: string;
  pbLastRoundType: string;
  pbLastSyncDate: string;
  pbLastSyncUser: string;
  pbLtmNoOfInvestments: null | number;
  pbNoOfActiveInvestments: null | number;
  pbOwnershipStatus: string;
  pbPreferredCompanyValuation: string;
  pbPreferredDealSize: string;
  pbPreferredEbitda: string;
  pbPreferredGeographies: string;
  pbPreferredIndustries: string;
  pbPreferredInvestmentAmount: string;
  pbPreferredInvestmentHorizon: string;
  pbPreferredInvestmentTypes: string;
  pbPreferredOther: string;
  pbPreferredRevenue: string;
  pbPreferredVerticals: string;
  pbPrimaryContact: string;
  pbPrimaryContactEmail: string;
  pbPrimaryContactPhone: string;
  pbPrimaryContactTitle: string;
  pbPrimaryUsRegion: string;
  pbYearFounded: string;
  pitchBookId: string;
  pitchBookLink: string;
}

export interface PitchbookNode {
  accountId?: { id: string; name: string } | null;
  currencyId?: { id: string; name: string } | null;
  id: string;
  pbAum?: null | number;
  pbAumBase?: null | number;
  pbBusinessStatus?: null | string;
  pbCompanyStatus?: null | string;
  pbDateOfLastRound?: null | string;
  pbDryPowder?: null | number;
  pbDryPowderBase?: null | number;
  pbEmployees?: null | number;
  pbFinancingStatus?: null | string;
  pbFirmSubType?: null | string;
  pbFirmType?: null | string;
  pbFormerName?: null | string;
  pbGlobalRegion?: null | string;
  pbGlobalSubRegion?: null | string;
  pbIndustriesAll?: null | string;
  pbIndustry?: null | string;
  pbIndustryCode?: null | string;
  pbIndustryGroup?: null | string;
  pbIndustrySector?: null | string;
  pbInvestorStatus?: null | string;
  pbLastInvDate?: null | string;
  pbLastInvLeadPartner?: null | string;
  pbLastInvLeadPartnerEmail?: null | string;
  pbLastInvLeadPartnerPhone?: null | string;
  pbLastInvLeadPartnerTitle?: null | string;
  pbLastInvLocation?: null | string;
  pbLastInvName?: null | string;
  pbLastInvPrimaryIndustry?: null | string;
  pbLastInvSize?: null | number;
  pbLastInvSizeBase?: null | number;
  pbLastInvStatus?: null | string;
  pbLastInvType?: null | string;
  pbLastRoundStatus?: null | string;
  pbLastRoundType?: null | string;
  pbLastSyncDate?: null | string;
  pbLastSyncUser?: { id: string; name: string } | null;
  pbLtmNoOfInvestments?: null | number;
  pbNoOfActiveInvestments?: null | number;
  pbOwnershipStatus?: null | string;
  pbPreferredCompanyValuation?: null | string;
  pbPreferredDealSize?: null | string;
  pbPreferredEbitda?: null | string;
  pbPreferredGeographies?: null | string;
  pbPreferredIndustries?: null | string;
  pbPreferredInvestmentAmount?: null | string;
  pbPreferredInvestmentHorizon?: null | string;
  pbPreferredInvestmentTypes?: null | string;
  pbPreferredOther?: null | string;
  pbPreferredRevenue?: null | string;
  pbPreferredVerticals?: null | string;
  pbPrimaryContact?: null | string;
  pbPrimaryContactEmail?: null | string;
  pbPrimaryContactPhone?: null | string;
  pbPrimaryContactTitle?: null | string;
  pbPrimaryUsRegion?: null | string;
  pbYearFounded?: null | string;
  pitchbookId?: null | string;
  pitchbookLink?: null | string;
  preferredMethodOfContact?: null | string;
}

// Helper
export function createEmptyPitchBookData(): PitchBookData {
  return {
    pbAum: '',
    pbAumBase: '',
    pbBusinessStatus: '',
    pbCompanyStatus: '',
    pbDateOfLastRound: '',
    pbDryPowder: '',
    pbDryPowderBase: '',
    pbEmployees: null,
    pbFinancingStatus: '',
    pbFirmSubType: '',
    pbFirmType: '',
    pbFormerName: '',
    pbGlobalRegion: '',
    pbGlobalSubRegion: '',
    pbIndustriesAll: '',
    pbIndustry: '',
    pbIndustryCode: '',
    pbIndustryGroup: '',
    pbIndustrySector: '',
    pbInvestorStatus: '',
    pbLastInvDate: '',
    pbLastInvLeadPartner: '',
    pbLastInvLeadPartnerEmail: '',
    pbLastInvLeadPartnerPhone: '',
    pbLastInvLeadPartnerTitle: '',
    pbLastInvLocation: '',
    pbLastInvName: '',
    pbLastInvPrimaryIndustry: '',
    pbLastInvSize: '',
    pbLastInvSizeBase: '',
    pbLastInvStatus: '',
    pbLastInvType: '',
    pbLastRoundStatus: '',
    pbLastRoundType: '',
    pbLastSyncDate: '',
    pbLastSyncUser: '',
    pbLtmNoOfInvestments: null,
    pbNoOfActiveInvestments: null,
    pbOwnershipStatus: '',
    pbPreferredCompanyValuation: '',
    pbPreferredDealSize: '',
    pbPreferredEbitda: '',
    pbPreferredGeographies: '',
    pbPreferredIndustries: '',
    pbPreferredInvestmentAmount: '',
    pbPreferredInvestmentHorizon: '',
    pbPreferredInvestmentTypes: '',
    pbPreferredOther: '',
    pbPreferredRevenue: '',
    pbPreferredVerticals: '',
    pbPrimaryContact: '',
    pbPrimaryContactEmail: '',
    pbPrimaryContactPhone: '',
    pbPrimaryContactTitle: '',
    pbPrimaryUsRegion: '',
    pbYearFounded: '',
    pitchBookId: '',
    pitchBookLink: ''
  };
}

// Mapper
export function mapPitchBookData(node: PitchbookNode): PitchBookData {
  return {
    pbAum: formatCurrency(node.pbAum),
    pbAumBase: formatCurrency(node.pbAumBase),
    pbBusinessStatus: node.pbBusinessStatus ?? '',
    pbCompanyStatus: node.pbCompanyStatus ?? '',
    pbDateOfLastRound: node.pbDateOfLastRound ?? '',
    pbDryPowder: formatCurrency(node.pbDryPowder),
    pbDryPowderBase: formatCurrency(node.pbDryPowderBase),
    pbEmployees: node.pbEmployees ?? null,
    pbFinancingStatus: node.pbFinancingStatus ?? '',
    pbFirmSubType: node.pbFirmSubType ?? '',
    pbFirmType: node.pbFirmType ?? '',
    pbFormerName: node.pbFormerName ?? '',
    pbGlobalRegion: node.pbGlobalRegion ?? '',
    pbGlobalSubRegion: node.pbGlobalSubRegion ?? '',
    pbIndustriesAll: node.pbIndustriesAll ?? '',
    pbIndustry: node.pbIndustry ?? '',
    pbIndustryCode: node.pbIndustryCode ?? '',
    pbIndustryGroup: node.pbIndustryGroup ?? '',
    pbIndustrySector: node.pbIndustrySector ?? '',
    pbInvestorStatus: node.pbInvestorStatus ?? '',
    pbLastInvDate: node.pbLastInvDate ?? '',
    pbLastInvLeadPartner: node.pbLastInvLeadPartner ?? '',
    pbLastInvLeadPartnerEmail: node.pbLastInvLeadPartnerEmail ?? '',
    pbLastInvLeadPartnerPhone: node.pbLastInvLeadPartnerPhone ?? '',
    pbLastInvLeadPartnerTitle: node.pbLastInvLeadPartnerTitle ?? '',
    pbLastInvLocation: node.pbLastInvLocation ?? '',
    pbLastInvName: node.pbLastInvName ?? '',
    pbLastInvPrimaryIndustry: node.pbLastInvPrimaryIndustry ?? '',
    pbLastInvSize: formatCurrency(node.pbLastInvSize),
    pbLastInvSizeBase: formatCurrency(node.pbLastInvSizeBase),
    pbLastInvStatus: node.pbLastInvStatus ?? '',
    pbLastInvType: node.pbLastInvType ?? '',
    pbLastRoundStatus: node.pbLastRoundStatus ?? '',
    pbLastRoundType: node.pbLastRoundType ?? '',
    pbLastSyncDate: node.pbLastSyncDate ?? '',
    pbLastSyncUser: node.pbLastSyncUser?.name ?? '',
    pbLtmNoOfInvestments: node.pbLtmNoOfInvestments ?? null,
    pbNoOfActiveInvestments: node.pbNoOfActiveInvestments ?? null,
    pbOwnershipStatus: node.pbOwnershipStatus ?? '',
    pbPreferredCompanyValuation: node.pbPreferredCompanyValuation ?? '',
    pbPreferredDealSize: node.pbPreferredDealSize ?? '',
    pbPreferredEbitda: node.pbPreferredEbitda ?? '',
    pbPreferredGeographies: node.pbPreferredGeographies ?? '',
    pbPreferredIndustries: node.pbPreferredIndustries ?? '',
    pbPreferredInvestmentAmount: node.pbPreferredInvestmentAmount ?? '',
    pbPreferredInvestmentHorizon: node.pbPreferredInvestmentHorizon ?? '',
    pbPreferredInvestmentTypes: node.pbPreferredInvestmentTypes ?? '',
    pbPreferredOther: node.pbPreferredOther ?? '',
    pbPreferredRevenue: node.pbPreferredRevenue ?? '',
    pbPreferredVerticals: node.pbPreferredVerticals ?? '',
    pbPrimaryContact: node.pbPrimaryContact ?? '',
    pbPrimaryContactEmail: node.pbPrimaryContactEmail ?? '',
    pbPrimaryContactPhone: node.pbPrimaryContactPhone ?? '',
    pbPrimaryContactTitle: node.pbPrimaryContactTitle ?? '',
    pbPrimaryUsRegion: node.pbPrimaryUsRegion ?? '',
    pbYearFounded: node.pbYearFounded ?? '',
    pitchBookId: node.pitchbookId ?? '',
    pitchBookLink: node.pitchbookLink ?? ''
  };
}

// Format number as currency string
// Note: formatCurrency is imported from @/lib/utils
